import { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Buildings, Moon, Sun, SignOut, List, X } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const CRUMB_LABELS = { businesses: "Businesses", projects: "Projects" };

function Breadcrumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split("/").filter(Boolean);
  return (
    <nav className="flex items-center gap-1.5 text-sm" data-testid="breadcrumbs">
      <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
        Home
      </Link>
      {segments.map((seg, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <span className="text-muted-foreground/50">/</span>
          <span className={i === segments.length - 1 ? "text-foreground font-medium font-data text-xs" : "text-muted-foreground"}>
            {CRUMB_LABELS[seg] || seg}
          </span>
        </span>
      ))}
    </nav>
  );
}

function Sidebar({ open, onClose }) {
  const { pathname } = useLocation();
  return (
    <>
      {open && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={onClose} />}
      <aside
        data-testid="sidebar"
        className={`fixed md:static z-40 h-full w-60 shrink-0 border-r border-border bg-card flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="flex items-center justify-between px-5 h-16 border-b border-border">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-sm bg-blue-800 dark:bg-blue-500 flex items-center justify-center">
              <span className="text-white font-display font-extrabold text-sm">W</span>
            </div>
            <div>
              <div className="font-display font-extrabold tracking-tight leading-none">Wydely</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Admin Console</div>
            </div>
          </Link>
          <button className="md:hidden text-muted-foreground hover:text-foreground" onClick={onClose} data-testid="sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <Link
            to="/"
            data-testid="nav-businesses"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
              pathname === "/" || pathname.startsWith("/businesses")
                ? "bg-blue-800 text-white dark:bg-blue-600"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}
          >
            <Buildings className="h-4 w-4" weight="bold" />
            Businesses
          </Link>
        </nav>
        <div className="p-4 border-t border-border text-[10px] uppercase tracking-widest text-muted-foreground">
          Internal Ops · v1.0
        </div>
      </aside>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("wydely_theme") === "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("wydely_theme", dark ? "dark" : "light");
  }, [dark]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 border-b border-border bg-card/70 backdrop-blur-xl flex items-center justify-between px-4 md:px-6 gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="md:hidden text-muted-foreground hover:text-foreground"
              onClick={() => setSidebarOpen(true)}
              data-testid="sidebar-open"
            >
              <List className="h-5 w-5" />
            </button>
            <Breadcrumbs />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              data-testid="theme-toggle"
              onClick={() => setDark((d) => !d)}
              className="text-muted-foreground hover:text-foreground"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu"
                  className="w-8 h-8 rounded-full bg-blue-800 dark:bg-blue-600 text-white text-xs font-bold flex items-center justify-center hover:opacity-90 transition-opacity"
                >
                  {(user?.email || "A")[0].toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-data text-xs truncate">{user?.email}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">{user?.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={handleLogout} className="text-red-600 focus:text-red-600 cursor-pointer">
                  <SignOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
